import { useEffect, useState, useMemo } from "react";
import api, { money, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, EmptyBox, Mini } from "./shared";
import { LifecycleTabs } from "@/components/lifecycle/LifecycleTabs";
import { ArchivedPanel } from "@/components/lifecycle/ArchivedPanel";
import { DollarSign, ShoppingBag, RefreshCcw, CheckCircle2, Undo2, Archive } from "lucide-react";

const STATUS_STYLES = {
  paid: "bg-green-500/15 text-green-300 border-green-500/30",
  refunded: "bg-red-500/15 text-red-300 border-red-500/30",
  pending: "bg-gold/15 text-gold border-gold/30",
};
const KINDS = ["all", "template", "deposit", "balance", "invoice"];

export function Orders() {
  const [items, setItems] = useState(null);
  const [kind, setKind] = useState("all");
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(null);
  const [lifeTab, setLifeTab] = useState("active");
  const [lifeVersion, setLifeVersion] = useState(0);

  const load = () => api.get("/orders").then((r) => setItems(r.data)).catch(() => setItems([]));
  const onLifeChanged = () => { load(); setLifeVersion((v) => v + 1); };
  useEffect(() => { load(); }, []);

  const stats = useMemo(() => {
    const paid = (items || []).filter((o) => o.payment_status === "paid");
    return { revenue: paid.reduce((s, o) => s + Number(o.amount || 0), 0), count: (items || []).length, paid: paid.length };
  }, [items]);

  const filtered = useMemo(() => (items || []).filter((o) => {
    if (kind !== "all" && o.kind !== kind) return false;
    if (q && !`${o.template_title || ""} ${o.email || ""} ${o.kind || ""} ${o.package_id || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, kind, q]);

  if (!items) return <Loader />;
  return (
    <div>
      <Head title="Orders" desc="Template purchases, deposits, and balances — fulfillment, linkage, and refunds." />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        <Mini label="Revenue (paid)" value={money(stats.revenue)} icon={DollarSign} />
        <Mini label="Paid orders" value={stats.paid} icon={CheckCircle2} />
        <Mini label="All orders" value={stats.count} icon={ShoppingBag} />
      </div>

      <div className="mb-5"><LifecycleTabs module="orders" value={lifeTab} onChange={setLifeTab} refreshKey={`${(items || []).length}-${lifeVersion}`} /></div>
      {lifeTab !== "active" ? <ArchivedPanel module="orders" status={lifeTab} singular="Order" allowDelete={true} onChanged={onLifeChanged} /> : (<>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex gap-1 bg-navy-800 border border-white/10 p-1" data-testid="order-kind-filter">
          {KINDS.map((k) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`order-filter-${k}`}
              className={`px-3 py-1.5 text-xs font-medium capitalize transition-colors ${kind === k ? "bg-gold text-navy" : "text-gray-400 hover:text-white"}`}>
              {k}{k === "all" ? ` (${items.length})` : ` (${items.filter((o) => o.kind === k).length})`}
            </button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search orders…" data-testid="order-search"
          className="bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white focus:border-gold focus:outline-none flex-1 min-w-[180px]" />
      </div>

      {filtered.length === 0 ? <EmptyBox text="No orders match your filters." /> : (
        <div className="bg-navy-800 border border-white/5 divide-y divide-white/5" data-testid="orders-list">
          {filtered.map((o) => (
            <OrderRow key={o.id} o={o} open={openId === o.id} onToggle={() => setOpenId(openId === o.id ? null : o.id)} onChanged={load} />
          ))}
        </div>
      )}
      </>)}
    </div>
  );
}

function OrderRow({ o, open, onToggle, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    onToggle();
    if (!open && detail === null) {
      try { const r = await api.get(`/admin/orders/${o.id}`); setDetail(r.data); } catch { setDetail(false); }
    }
  };
  const fulfill = async () => {
    setBusy(true);
    try { const r = await api.post(`/admin/orders/${o.id}/fulfill`); toast.success(r.data.fulfilled ? "Marked fulfilled." : "Marked unfulfilled."); onChanged?.(); const d = await api.get(`/admin/orders/${o.id}`); setDetail(d.data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const refund = async () => {
    if (!window.confirm(`Refund ${money(o.amount)} to ${o.email || "customer"}? (Stripe test mode)`)) return;
    setBusy(true);
    try { await api.post(`/admin/orders/${o.id}/refund`); toast.success("Refund issued via Stripe."); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const archive = async () => {
    if (!window.confirm("Archive this order? Orders are accounting records — recoverable anytime from the Records Center. Once archived, test orders can be permanently deleted there or here.")) return;
    setBusy(true);
    try { await api.post(`/lifecycle/orders/${o.id}/archive`, { reason: "" }); toast.success("Order archived. Recoverable in Records Center."); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid={`order-${o.id}`}>
      <div className="p-5 flex items-center justify-between gap-4 cursor-pointer hover:bg-white/5" onClick={toggle} data-testid={`order-toggle-${o.id}`}>
        <div className="min-w-0">
          <div className="text-white font-medium truncate">{o.template_title || `${o.kind} — ${o.package_id || ""}`}</div>
          <div className="text-gray-500 text-sm truncate">{o.email || "guest"} · {o.kind}{o.fulfilled ? " · fulfilled" : ""}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading text-lg text-white">{money(o.amount)}</div>
          <span data-testid={`order-status-${o.id}`} className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border rounded-full ${STATUS_STYLES[o.payment_status] || STATUS_STYLES.pending}`}>{o.payment_status}</span>
        </div>
      </div>
      {open && (
        <div className="px-5 pb-5" data-testid={`order-detail-${o.id}`}>
          <div className="bg-navy border border-white/10 p-4 text-sm space-y-2">
            {detail === null ? <p className="text-gray-500">Loading…</p> : detail === false ? <p className="text-red-300">Could not load order.</p> : (
              <>
                <Line k="Customer" v={detail.email || "guest"} />
                {detail.license_key && <Line k="License key" v={<span className="font-mono">{detail.license_key}</span>} />}
                {detail.template && <Line k="Template" v={`${detail.template.title}${detail.template.current_version ? ` · v${detail.template.current_version}` : ""}`} />}
                {detail.invoice && <Line k="Linked invoice" v={`${detail.invoice.number || ""} · ${detail.invoice.status}`} />}
                {detail.project && <Line k="Project" v={`${detail.project.org_name} · ${detail.project.stage}`} />}
                <Line k="Downloads" v={detail.download_count ?? 0} />
                <Line k="Fulfilled" v={detail.fulfilled ? "Yes" : "No"} />
                <div className="flex flex-wrap gap-2 pt-2">
                  <button onClick={fulfill} disabled={busy} data-testid={`order-fulfill-${o.id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-white/10 text-gray-200 hover:border-gold/50 hover:text-white transition-colors disabled:opacity-40"><CheckCircle2 size={13} className="text-gold" /> {detail.fulfilled ? "Mark unfulfilled" : "Mark fulfilled"}</button>
                  {o.payment_status === "paid" && <button onClick={refund} disabled={busy} data-testid={`order-refund-${o.id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-500/30 text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-40"><Undo2 size={13} /> Refund</button>}
                  <button onClick={archive} disabled={busy} data-testid={`order-archive-${o.id}`} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-white/10 text-gray-300 hover:border-gold/50 hover:text-white transition-colors disabled:opacity-40"><Archive size={13} /> Archive</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Line = ({ k, v }) => (
  <div className="flex justify-between gap-3"><span className="text-gray-500">{k}</span><span className="text-gray-200 text-right">{v}</span></div>
);
